import { CircleWithLineIcon } from '@carboncopyco/icons';
import { ColorHex, Separator } from '@carboncopyco/ui';
import { getMaxZIndex } from '@carboncopyco/utils';
import classNames from 'classnames';
import type React from 'react';
import type {
  DropResult,
  OnDragEndResponder,
  OnDragStartResponder,
} from 'react-beautiful-dnd';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { Droppable } from './Droppable';

interface DraggableItem<T> {
  id: string;
  item: T;
}

export interface DragEndArgs {
  sourceIndex: DropResult['source']['index'];
  destinationIndex: DropResult['destination']['index'];
  id: DropResult['source']['droppableId'];
}

export interface DraggableListProps<T> {
  disabled?: boolean;
  onDragStart?: () => void;
  onDragEnd: (args: DragEndArgs) => void;
  items: DraggableItem<T>[];
  renderItem: (
    item: DraggableItem<T>['item'],
    index: number
  ) => React.ReactNode;
  withLeftBorder?: boolean;
}

// We extracted the render function so we could pass it to the "renderClone" prop of Droppable component.
// It was done in order to support D&D when the parent component has a transform CSS style.
// https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/reparenting.md#reparenting-a-draggable-
/* eslint-disable */
const getRenderItem = (items, renderItem) => (provided, snapshot, rubric) => (
  <div
    ref={provided.innerRef}
    {...provided.draggableProps}
    {...provided.dragHandleProps}
    role="listitem"
    className={classNames('group', snapshot.isDragging && 'dragging')}
    style={{
      userSelect: 'none',
      margin: '0 0 10px 0',
      boxShadow: snapshot.isDragging
        ? 'rgb(0 0 0 / 30%) 2px 4px 14px'
        : undefined,
      borderRadius: snapshot.isDragging ? '0.375rem' : undefined,
      ...provided.draggableProps.style,
      zIndex: snapshot.isDragging ? getMaxZIndex() + 1 : undefined,
    }}
  >
    {renderItem(items[rubric.source.index].item, rubric.source.index)}
  </div>
);

export const DraggableList = <T,>({
  disabled,
  onDragStart,
  onDragEnd,
  items,
  withLeftBorder,
  renderItem,
}: DraggableListProps<T>) => {
  const dragStarted: OnDragStartResponder = () => {
    onDragStart?.();
  };

  const dragEnded: OnDragEndResponder = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    if (result.source.index === result.destination.index) {
      return;
    }

    onDragEnd({
      sourceIndex: result.source.index,
      destinationIndex: result.destination.index,
      id: result.source.droppableId,
    });
  };

  const renderDraggableItem = getRenderItem(items, renderItem);

  return (
    <DragDropContext
      onDragStart={dragStarted}
      onDragEnd={dragEnded}
    >
      <Droppable
        droppableId="droppable"
        renderClone={renderDraggableItem}
      >
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className={classNames(
              'flex flex-col',
              withLeftBorder
                ? 'pt-2.5 -mx-2.5 w-[calc(100%+1.25rem)] relative '
                : 'w-full'
            )}
            role="list"
          >
            {withLeftBorder && (
              <div className="absolute left-0 bottom-[27px] -top-2 w-px bg-ds-primary-300" />
            )}
            {items.map((item, index) => (
              <div
                key={item.id}
                className={classNames(
                  'w-full min-w-0 flex items-center',
                  withLeftBorder && 'relative'
                )}
              >
                {withLeftBorder && (
                  <>
                    <div className=" flex-col flex-shrink-0 self-start top-[17px] relative">
                      <div className="relative top-2">
                        <Separator
                          horizontal
                          color="bg-ds-primary-300"
                        />
                      </div>
                      <div className="relative left-1">
                        <CircleWithLineIcon
                          color={ColorHex.primary300}
                          size={15}
                        />
                      </div>
                    </div>
                  </>
                )}
                <div className="flex-grow min-w-0 ">
                  <Draggable
                    key={item.id}
                    draggableId={item.id}
                    index={index}
                    isDragDisabled={disabled}
                  >
                    {renderDraggableItem}
                  </Draggable>
                </div>
                {withLeftBorder && index === items.length - 1 && (
                  <div className="absolute left-0 top-[26px] bottom-0 w-px bg-white" />
                )}
              </div>
            ))}

            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
